<script setup lang='ts'>
import { Heart, Share2 } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Language } from '@/types/locale';

import TheStarRating from '@/components/mode-list/TheStarRating.vue';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { convertUrlToResizer, formatDisplayRating } from '@/utils/utils';

import MobileSlider from './MobileSlider.vue';
import Popover from './Popover.vue';

type HeaderProps = {
  title: string;
  source: string;
  rating: {
    ratingReviewCount: number;
    rating: string;
    ratingSource: string;
  } | null;
  images: Array<{ credit: string | null; lang: Language; name: string; url: string }>;
};

const props = defineProps<{
  shareLinks: Array<{ name: string; href: string; imgSrc: string }>;
  mobileHeaderData: HeaderProps;
}>();

const emits = defineEmits<{
  toggleFavorite: [void];
  scrollToFairguestWidget: [void];
}>();

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, componentsBaseUrl } = storeToRefs(globalConfigurationStore);
const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

const rating = computed(() => props.mobileHeaderData.rating?.rating ?? 0);
const displayedRating = computed(() => formatDisplayRating(+rating.value));
</script>

<template>
  <section class="mobile_header" :aria-label="t('aria.presentation')">
    <!-- <div role="link" class="backto">
      <ArrowLeft :size="18" aria-hidden="true" />
      <p>{{ t('back') }}</p>
    </div> -->
    <div style="position:relative;">
      <MobileSlider v-if="props.mobileHeaderData.images?.length >= 2" :images="props.mobileHeaderData.images" />
      <img
        v-else-if="props.mobileHeaderData.images?.length"
        :src="convertUrlToResizer(props.mobileHeaderData.images[0].url)" :alt="props.mobileHeaderData.images[0].credit ?? props.mobileHeaderData.title" class="carousel-image"
        fetchpriority="high" height="270" width="355" draggable="false"
      >
      <div class="slider_actions">
        <button
          v-if="showFavorites"
          id="favorites" type="button" class="slider_action" :aria-label="t('cta.add_to_favorites')"
          @click="emits('toggleFavorite')" @keydown.enter="emits('toggleFavorite')"
        >
          <Heart :size="20" aria-hidden="true" style="position:relative;top:1px;stroke:var(--yb-color-black);" />
        </button>
        <Popover :items="shareLinks" :title="t('popover.share') ">
          <template #button>
            <button type="button" class="slider_action" :aria-label="t('cta.share')">
              <Share2 :size="20" aria-hidden="true" style="position:relative;top:1px;stroke:var(--yb-color-black);" />
            </button>
          </template>
        </Popover>
      </div>
    </div>

    <h1 class="title">
      {{ props.mobileHeaderData.title }}
    </h1>

    <div v-if="props.mobileHeaderData.rating" class="rating_wrapper">
      <template v-if="props.mobileHeaderData.rating.ratingSource === 'FAIRGUEST'">
        <TheStarRating :rating="displayedRating" />
        <p class="rating">
          {{ props.mobileHeaderData.rating.rating }} / 10
        </p>
        <div
          role="link" class="reviews_count" tabindex="0" @click="emits('scrollToFairguestWidget')"
          @keydown.enter="emits('scrollToFairguestWidget')"
        >
          {{ props.mobileHeaderData.rating.ratingReviewCount }} {{ t('content.reviews') }}
        </div>
      </template>
      <template v-else>
        <div class="external-rating">
          <svg class="external-star" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
          <p class="rating">
            {{ props.mobileHeaderData.rating.rating }}
          </p>
          <span class="review-count">({{ props.mobileHeaderData.rating.ratingReviewCount }})</span>
          <img v-if="props.mobileHeaderData.rating.ratingSource === 'GOOGLE'" :src="`${componentsBaseUrl}/logo_google.jpg`" height="20" width="20" alt="source des avis" loading="lazy">
          <Popover :title="t('rating.title')" :content="t('rating.content')">
            <template #button>
              <p class="rating_note">
                {{ t('rating.title') }}
              </p>
            </template>
          </Popover>
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.backto {
  display: flex;
  align-items: center;
  padding-block: var(--yb-spacing-sm);
  gap: var(--yb-spacing-xs);
  cursor: pointer;
}

.mobile_header {
  background-color: var(--yb-color-white);
  text-align: left;

  @media screen and (width > 767px) {
    display: none;
  }
}

.carousel-image {
  display: block; /* Removes bottom space under image */
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  pointer-events: none; /* Prevents dragging the image itself */
  border-radius: var(--yb-card-radius);
}

.slider_actions {
  position: absolute;
  top: var(--yb-spacing-sm);
  right: var(--yb-spacing-sm);
  display: flex;
  align-items: center;

  .slider_action {
    appearance: none;
    display: grid;
    place-content: center;
    background-color: var(--yb-color-white);
    /* width: 32px; */
    border-radius: 50%;
    padding: var(--yb-spacing-xs);
    border: none;
    outline-color: var(--yb-color-primary);
    cursor: pointer;
  }

  /* Use this instead of gap property on slider_actions, for correct rendering on mobile */

  .slider_action:not(:last-of-type) {
    margin-right: 8px;
  }

  &.selected {
    background-color: var(--yb-color-primary);

    svg {
      fill: var(--yb-color-white);
    }
  }
}

.title {
  font-size: 32px;
  font-weight: var(--yb-text-bold);
  padding-block: var(--yb-spacing-sm);
}

.rating_wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: var(--yb-color-black);
  gap: var(--yb-spacing-sm);
  margin-bottom: var(--yb-spacing-sm);

  .rating {
    font-weight: var(--yb-text-bold);
    font-size: 16px;
  }

  .reviews_count {
    font-weight: var(--yb-text-light);
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
  }

  .external-rating {
    display: flex;
    align-items: center;
    gap: 4px;

    .rating_note {
      color: var(--yb-color-black);
      font-weight: 400;
      font-size: 14px;
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: var(--yb-color-black);
    }
  }

  .external-star {
    width: 20px;
    height: 20px;
    fill: #ff9800;
    flex-shrink: 0;
  }

  .review-count {
    font-weight: var(--yb-text-light);
    font-size: 14px;
  }
}
</style>
